
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';

interface PendingUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  created_at: string;
  approval_status: string;
  role: string;
}

interface UserApprovalTableProps {
  users: PendingUser[];
  userType: string;
  onApprove: (userId: string, role: string) => Promise<void>;
  onReject: (userId: string, role: string) => Promise<void>;
  actionLoading: string | null;
}

export const UserApprovalTable = ({ 
  users, 
  userType, 
  onApprove, 
  onReject, 
  actionLoading 
}: UserApprovalTableProps) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('hy-AM', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (users.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Սպասող {userType === 'lecturer' ? 'դասախոսներ' : 'ուսանողներ'} չկան
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-border/70">
            <TableHead className="text-muted-foreground">Անուն Ազգանուն</TableHead>
            <TableHead className="text-muted-foreground">Էլ. փոստ</TableHead>
            <TableHead className="text-muted-foreground">Հեռախոս</TableHead>
            <TableHead className="text-muted-foreground">Գրանցման ամսաթիվ</TableHead>
            <TableHead className="text-muted-foreground">Կարգավիճակ</TableHead>
            <TableHead className="text-muted-foreground">Գործողություններ</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id} className="border-border/70">
              <TableCell className="text-muted-foreground">
                {user.first_name} {user.last_name}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {user.email}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {user.phone || '-'}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {formatDate(user.created_at)}
              </TableCell>
              <TableCell>
                <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-400/30">
                  <Clock className="h-3 w-3 mr-1" />
                  Սպասող
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Button
                    onClick={() => onApprove(user.id, user.role)}
                    size="sm"
                    disabled={actionLoading === `approve-${user.id}` || actionLoading === `reject-${user.id}`}
                    className="bg-primary hover:bg-primary/90"
                  >
                    {actionLoading === `approve-${user.id}` ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-1" />
                    )}
                    Հաստատել
                  </Button>
                  <Button
                    onClick={() => onReject(user.id, user.role)}
                    size="sm"
                    variant="destructive"
                    disabled={actionLoading === `approve-${user.id}` || actionLoading === `reject-${user.id}`}
                    className="font-mono"
                  >
                    {actionLoading === `reject-${user.id}` ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <XCircle className="h-4 w-4 mr-1" />
                    )}
                    Մերժել
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
